import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import API_ENDPOINTS from "../apiConfig";
import { downloadViaAuthFetch } from "../utils/downloadFile";
import { getLanguage } from "../utils/localStorageHelper";

const todayStr = () => new Date().toISOString().split("T")[0];
const firstOfMonthStr = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split("T")[0];
};
// date-string arithmetic — handles leap years correctly, unlike a fixed day count
const addYears = (dateStr, years) => {
  const d = new Date(dateStr + "T00:00:00");
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().split("T")[0];
};

/**
 * Passings (statistics) CSV export controls. Shared by the statistics page and
 * site settings → Export.
 *
 * areaId: "all" or an area id. Pass onAreaChange to show an area picker here;
 * without it the caller owns the area (statistics page uses its own dropdown).
 */
function PassingsExportForm({ siteId, siteName, areas, areaId, onAreaChange, onDone }) {
  const { t } = useTranslation();
  const today = todayStr();

  const [exportStart, setExportStart] = useState(firstOfMonthStr());
  const [exportEnd, setExportEnd] = useState(today);
  const [includeDenied, setIncludeDenied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  // the furthest end date allowed for a given start, capped at today — used to
  // auto-clamp the other field so a 1-year range can never silently be exceeded
  const maxExportEndFor = (start) => {
    const oneYearLater = addYears(start, 1);
    return oneYearLater < today ? oneYearLater : today;
  };

  const handleStartChange = (value) => {
    setExportStart(value);
    const cap = maxExportEndFor(value);
    if (exportEnd > cap) setExportEnd(cap); // keep the range at or under 1 year, no popup
  };
  const handleEndChange = (value) => {
    setExportEnd(value);
    const floor = addYears(value, -1);
    if (exportStart < floor) setExportStart(floor); // same, from the other direction
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError("");
    try {
      const url = new URL(API_ENDPOINTS.exportPassingsCsv.replace("##siteid##", siteId));
      url.searchParams.set("startDate", exportStart);
      url.searchParams.set("endDate", exportEnd);
      if (areaId !== "all") url.searchParams.set("areaId", areaId);
      if (includeDenied) url.searchParams.set("includeDenied", "true");
      url.searchParams.set("lang", getLanguage()); // German spreadsheets expect ';' — see PassingsController::exportCsv

      const safeName = (siteName || "site").replace(/[^a-z0-9]+/gi, "_");
      const filename = `passings_${safeName}_${exportStart}_${exportEnd}.csv`;

      await downloadViaAuthFetch(url.toString(), filename);
      if (onDone) onDone();
    } catch (e) {
      setExportError(t("export_failed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="row g-2 align-items-end">
        {onAreaChange && (
          <div className="col-12">
            <label className="form-label small text-muted mb-1" htmlFor="passings-export-area">{t("Area")}</label>
            <select
              id="passings-export-area"
              className="form-select form-select-sm"
              value={areaId}
              onChange={(e) => onAreaChange(e.target.value)}
            >
              <option value="all">{t("All areas")}</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="col-6 col-md-3">
          <label className="form-label small text-muted mb-1">{t("date_from")}</label>
          <input
            type="date" className="form-control form-control-sm"
            value={exportStart} max={exportEnd}
            onChange={(e) => handleStartChange(e.target.value)}
          />
        </div>
        <div className="col-6 col-md-3">
          <label className="form-label small text-muted mb-1">{t("date_to")}</label>
          <input
            type="date" className="form-control form-control-sm"
            value={exportEnd} min={exportStart} max={today}
            onChange={(e) => handleEndChange(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-3 d-flex align-items-center">
          <div className="form-check">
            <input
              className="form-check-input" type="checkbox" id="exportIncludeDenied"
              checked={includeDenied}
              onChange={(e) => setIncludeDenied(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="exportIncludeDenied">
              {t("include_denied")}
            </label>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <button
            className="btn btn-sm ez-button-full w-100"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? t("Loading...") : (
              <><i className="bi bi-download me-1"></i>{t("export_csv")}</>
            )}
          </button>
        </div>
      </div>
      <div className="text-muted small mt-2">
        {areaId === "all"
          ? t("All areas")
          : (areas.find((a) => String(a.id) === String(areaId))?.name || "")}
        <span className="mx-2">·</span>
        {t("export_range_hint")}
      </div>
      {exportError && <div className="text-danger small mt-2">{exportError}</div>}
    </>
  );
}

export default PassingsExportForm;
